import { useState } from "react";
import type { ForwardingSettings, UserTemplate } from "@lucralink/shared";
import { forwardingErrorMessage } from "@/lib/forwarding";
import { useForwardingSettings, useSaveForwardingSettings } from "@/lib/hooks/useForwarding";
import {
  useDeleteTextTemplate,
  useSaveTextTemplate,
  useTextTemplates,
} from "@/lib/hooks/useTextTemplates";
import { InlineError, Segmented } from "@/components/forwarding/controls";
import { TemplateEditor } from "./TemplateEditor";
import { UpgradePrompt } from "@/components/billing/UpgradePrompt";
import { useEntitlements } from "@/lib/entitlements";
import { useTenant } from "@/lib/hooks/useTenant";
import { Gallery, MyTemplates, PlatformDefaults } from "./TemplateViews";

type View = "gallery" | "mine" | "platforms";
type EditorState = { id: string | null; name: string; body: string } | null;
type Run = (action: () => Promise<unknown>, after?: () => void) => Promise<void>;

/** Executa uma acao assincrona guardando a mensagem de erro (ou limpando antes). */
function useRunner(): { error: string | null; run: Run } {
  const [error, setError] = useState<string | null>(null);
  const run: Run = async (action, after) => {
    setError(null);
    try {
      await action();
      after?.();
    } catch (err) {
      setError(forwardingErrorMessage(err));
    }
  };
  return { error, run };
}

function SectionHeader({
  showTabs,
  view,
  onView,
}: {
  showTabs: boolean;
  view: View;
  onView: (v: View) => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 id="templates-title" className="font-semibold text-foreground">
          Templates de texto
        </h2>
        <p className="text-xs text-muted-foreground">
          Como a oferta encaminhada aparece no Telegram e no WhatsApp.
        </p>
      </div>
      {showTabs && (
        <Segmented
          label="Seção de templates"
          value={view}
          onChange={onView}
          options={[
            { value: "gallery", label: "Modelos prontos" },
            { value: "mine", label: "Meus templates" },
            { value: "platforms", label: "Por plataforma" },
          ]}
        />
      )}
    </div>
  );
}

function TemplatesBody({
  view,
  settings,
  templates,
  run,
  onEditor,
}: {
  view: View;
  settings: ForwardingSettings;
  templates: readonly UserTemplate[];
  run: Run;
  onEditor: (state: EditorState) => void;
}): JSX.Element {
  const deleteTemplate = useDeleteTextTemplate();
  const saveSettings = useSaveForwardingSettings();
  const updateSettings = (next: ForwardingSettings): void =>
    void run(() => saveSettings.mutateAsync(next));
  const setDefault = (ref: string): void =>
    updateSettings({ ...settings, defaultTemplateRef: ref });
  const { data: tenantBilling } = useTenant();
  // Quem caiu para o Free continua vendo (e podendo apagar) os templates que ja tinha.
  const canCreate = useEntitlements(tenantBilling?.tenant).hasCustomTemplates || templates.length > 0;

  if (view === "gallery") {
    return (
      <Gallery
        settings={settings}
        onUseAsBase={(name, body) => onEditor({ id: null, name, body })}
        onSetDefault={setDefault}
      />
    );
  }
  if (view === "mine" && !canCreate) {
    return <UpgradePrompt reason="customTemplates" variant="inline" />;
  }
  if (view === "mine") {
    return (
      <MyTemplates
        templates={templates}
        defaultRef={settings.defaultTemplateRef}
        onNew={() =>
          onEditor({ id: null, name: "", body: "**{title}**\n{priceText}\n{affiliateUrl}" })
        }
        onEdit={(t) => onEditor({ id: t.id, name: t.name, body: t.body })}
        onDelete={(id) => void run(() => deleteTemplate.mutateAsync(id))}
        onSetDefault={setDefault}
      />
    );
  }
  return <PlatformDefaults settings={settings} templates={templates} onChange={updateSettings} />;
}

/** Personalizacao > Templates de texto. */
export function TemplatesSection(): JSX.Element {
  const [view, setView] = useState<View>("gallery");
  const [editor, setEditor] = useState<EditorState>(null);
  const { error, run } = useRunner();
  const { data: settings } = useForwardingSettings();
  const { data: templates = [] } = useTextTemplates();
  const saveTemplate = useSaveTextTemplate();

  return (
    <section
      className="space-y-4 rounded-xl border border-border bg-card p-5"
      aria-labelledby="templates-title"
    >
      <SectionHeader showTabs={!editor} view={view} onView={setView} />
      <InlineError message={editor ? null : error} />
      {editor && (
        <TemplateEditor
          key={editor.id ?? "novo"}
          initialName={editor.name}
          initialBody={editor.body}
          saving={saveTemplate.isPending}
          error={error}
          onCancel={() => setEditor(null)}
          onSave={(name, body) =>
            void run(
              () => saveTemplate.mutateAsync({ id: editor.id, name, body }),
              () => {
                setEditor(null);
                setView("mine");
              },
            )
          }
        />
      )}
      {!editor && settings && (
        <TemplatesBody
          view={view}
          settings={settings}
          templates={templates}
          run={run}
          onEditor={setEditor}
        />
      )}
    </section>
  );
}
